import * as Sentry from "@sentry/vue";

export class SentryService {
  // Проверяем, включен ли Sentry
  private static get isEnabled(): boolean {
    return import.meta.env.VITE_SENTRY_ENABLED === 'true' && !!import.meta.env.VITE_SENTRY_DSN;
  }

  static captureException(error: Error, context?: Record<string, any>) {
    if (!this.isEnabled) {
      console.log('Sentry отключен, ошибка только в консоли:', error, context);
      return;
    }
    
    Sentry.captureException(error, {
      extra: context,
    });
  }

  static captureMessage(message: string, level: 'error' | 'warning' | 'info' | 'debug' = 'info') {
    if (!this.isEnabled) {
      console.log('Sentry отключен, сообщение только в консоли:', message, level);
      return;
    }
    
    Sentry.captureMessage(message, level);
  }

  static setUser(user: { id?: string; email?: string; username?: string }) {
    if (!this.isEnabled) {
      console.log('Sentry отключен, пользователь не установлен:', user);
      return;
    }
    
    Sentry.setUser(user);
  }

  static setTag(key: string, value: string) {
    if (!this.isEnabled) {
      console.log('Sentry отключен, тег не установлен:', key, value);
      return;
    }
    
    Sentry.setTag(key, value);
  }

  static setContext(key: string, context: Record<string, any>) {
    if (!this.isEnabled) {
      console.log('Sentry отключен, контекст не установлен:', key, context);
      return;
    }
    
    Sentry.setContext(key, context);
  }

  static addBreadcrumb(message: string, category?: string, level?: 'error' | 'warning' | 'info' | 'debug') {
    if (!this.isEnabled) {
      console.log('Sentry отключен, breadcrumb не добавлен:', message, category, level);
      return;
    }
    
    Sentry.addBreadcrumb({
      message,
      category: category || 'custom',
      level: level || 'info',
      timestamp: Date.now(),
    });
  }

  // Создаем тестовые события для проверки
  static testError() {
    try {
      throw new Error('Тестовая ошибка от VPN без проблем!');
    } catch (error) {
      this.captureException(error as Error, {
        testType: 'manual_test',
        timestamp: new Date().toISOString(),
      });
    }
  }

  static testMessage() {
    this.captureMessage('Тестовое сообщение от VPN без проблем!', 'info');
  }

  // Информация о состоянии Sentry
  static getStatus() {
    return {
      enabled: this.isEnabled,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    };
  }

  // Методы для пользовательских сценариев
  static trackUserAction(action: string, context?: Record<string, any>) {
    this.captureMessage(`Пользовательское действие: ${action}`, 'info');
    if (context) {
      this.setContext(`user_action_${action}`, context);
    }
  }

  static trackPageView(page: string, context?: Record<string, any>) {
    this.captureMessage(`Просмотр страницы: ${page}`, 'info');
    if (context) {
      this.setContext(`page_view_${page}`, context);
    }
  }

  static trackModalOpen(modalName: string, context?: Record<string, any>) {
    this.captureMessage(`Открыта модалка: ${modalName}`, 'info');
    if (context) {
      this.setContext(`modal_open_${modalName}`, context);
    }
  }

  static trackModalClose(modalName: string, context?: Record<string, any>) {
    this.captureMessage(`Закрыта модалка: ${modalName}`, 'info');
    if (context) {
      this.setContext(`modal_close_${modalName}`, context);
    }
  }

  static trackButtonClick(buttonName: string, context?: Record<string, any>) {
    this.captureMessage(`Нажата кнопка: ${buttonName}`, 'info');
    if (context) {
      this.setContext(`button_click_${buttonName}`, context);
    }
  }

  static trackNavigation(from: string, to: string, context?: Record<string, any>) {
    this.captureMessage(`Навигация: ${from} → ${to}`, 'info');
    if (context) {
      this.setContext(`navigation_${from}_to_${to}`, context);
    }
  }

  static trackError(error: Error, context?: Record<string, any>, userAction?: string) {
    this.captureException(error, {
      ...context,
      user_action: userAction || 'unknown',
      error_timestamp: new Date().toISOString(),
    });
  }
}
